
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import _ from 'lodash';
import { concatMap, delay, forkJoin, from, of, Observable } from 'rxjs';
import { RecurrenceConfig } from '../standing-series/standing-series.component';
import { StandingSeriesSaveService, StandingSeriesSaveResult, StandingSeriesSaveOptions } from '../../../services/standing-series-save.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-event-schedules-entity',
  templateUrl: './event-schedules-entity.component.html',
  styleUrls: ['./event-schedules-entity.component.css']
})
export class EventSchedulesEntityComponent extends BaseDialogComponent implements OnInit {
  override eckType = "events";
  override bundle = "schedules";
  override entityId = this._injectedDialogData['data']['EntityID'];

  // Recurring series state. A schedules create form can select MULTIPLE
  // instructors; each becomes its own standing_series (a series carries one
  // field_instructor). So we track an array of pending series ids and, on
  // conflict, aggregate per-instructor.
  //
  // The service is built in ngOnInit from the base class's already-injected
  // REST services. Not via inject()/field initializer (Angular 13 NG0203) and
  // not a template-reachable getter (NG0100). See the lesson/services dialogs
  // for the same pattern.
  private _seriesSave!: StandingSeriesSaveService;
  isRecurring = false;
  recurrenceConfig: RecurrenceConfig | null = null;
  seriesConflicts: any[] = [];
  pendingSeriesIds: number[] = [];
  /** Snapshot of the save options per pending series, keyed by series id, for the retry. */
  private _pendingOptsById = new Map<number, StandingSeriesSaveOptions>();

  override ngOnInit(): void {
    super.ngOnInit();
    this._seriesSave = new StandingSeriesSaveService(
      this._entityRESTService,
      this._drupalRESTService,
    );
  }

  onRecurrenceChanged(config: RecurrenceConfig | null): void {
    this.isRecurring = !!config;
    this.recurrenceConfig = config;
    this.seriesConflicts = [];
  }

  /** Template helper for the <app-standing-series> studentAccountId binding (schedules have none). */
  extractIdFromAutocomplete(value: string): string {
    if (!value) return '';
    if (!isNaN(+value)) return value;
    const match = /\(([^)]+)\)(?!.*\()/.exec(value);
    return match ? match[1] : value;
  }

  handleDateTimeChange(value: any) {
    if (value) {
      const momentDate = moment(value);
      this.onDateChange(momentDate);
      this.uiUpdateDate(momentDate);
    }
  }

  override onSubmit(form): void {
    if (!form?.value?.field_type) {
      form?.controls?.field_type?.markAsTouched();
      this.errorMessage = 'Select a Schedule Type.';
      return;
    }
    this.errorMessage = '';

    // Recurring branch: create one standing_series per selected instructor and
    // generate, instead of writing one-off schedule events.
    if (this.isRecurring && this.recurrenceConfig && this.actionType === 'create') {
      this.saveAsRecurring(form);
      return;
    }

    this.errorMessage = "";
    this.displayProgressSpinner(true);

    let values;
    values = _.cloneDeep(form.value);
    this.__alterValues(values);

    // Check for create mode.
    if (this.actionType == "create") {
      this.displayProgressSpinner(true);

      from(values.field_schedule_instructor).pipe(
        concatMap(instructorId => {
          const instructorValues = { ...values, field_schedule_instructor: instructorId };
          return this._entityRESTService.createEntity(this.eckType, this.bundle, instructorValues);
        })
      ).subscribe({
        next: data => LoggerService.log('data', data),
        error: error => {
          this.handleError(error);
          this.displayProgressSpinner(false);
        },
        complete: () => {
          this.closeDialog();
          this.displayProgressSpinner(false);
        }
      });
    }

    if (this.actionType == "edit") {
      this.displayProgressSpinner(true);

      this._entityRESTService.patchEntity(this.eckType, this.bundle, this.entityId, values)
        .subscribe(data => {
          LoggerService.log('data', data);
          this.closeDialog();
          this.displayProgressSpinner(false);
        },
          error => this.handleError(error)
        )
    }

  }

  get sortedScheduleTypes() {
    if (!this._taxonomyService.schedule_type) {
      return [];
    }
    return [...this._taxonomyService.schedule_type].sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  // --- Recurring series methods (multi-instructor) ---

  /** Human-readable instructor name for a uid, for tagging conflicts. */
  private instructorName(uid: string): string {
    const match = (this._fieldsService.instructorData() || []).find((i: any) => String(i.uid) === String(uid));
    return match?.value || `Instructor ${uid}`;
  }

  /** Selected instructor uids from the multi-select (always normalize to an array). */
  private selectedInstructorIds(form: any): string[] {
    const raw = form?.value?.field_schedule_instructor;
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return raw ? [raw] : [];
  }

  /** Build save options for one instructor's series. Schedules carry no student. */
  private buildSeriesSaveOptions(form: any, instructorId: string): StandingSeriesSaveOptions {
    const startDateObj = this.field_date_and_time ? new Date(this.field_date_and_time) : new Date();
    const startDate = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;
    return {
      bundle: 'schedules',
      noun: 'schedules',
      operatorId: instructorId,
      includeEnrollment: false,
      config: this.recurrenceConfig!,
      attendee: {},
      studentId: '',
      studioId: this._authService.selectedStudio?.id,
      time: this.field_date_and_time ? new Date(this.field_date_and_time).toTimeString().substring(0, 5) : '12:00',
      startDate,
      duration: form?.value?.field_duration || '00:45',
      typeId: form?.value?.field_type || '',
      notes: form?.value?.field_notes || '',
    };
  }

  private saveAsRecurring(form: any): void {
    if (this.isSaving) return;
    const instructorIds = this.selectedInstructorIds(form);
    if (instructorIds.length === 0) {
      this.errorMessage = 'Select at least one instructor.';
      return;
    }
    this.isSaving = true;
    this.seriesConflicts = [];
    // A re-save while conflicts are showing starts fresh series, so retire the
    // ones still pending from the previous attempt.
    for (const seriesId of this.pendingSeriesIds) {
      this._seriesSave.cancel(seriesId).subscribe(() => {}, () => {});
    }
    this.pendingSeriesIds = [];
    this._pendingOptsById.clear();

    // One series per instructor, in parallel. Each emits a per-instructor
    // result we tag with the instructor name before aggregating.
    const calls = instructorIds.map(uid => {
      const opts = this.buildSeriesSaveOptions(form, uid);
      return new Observable<{ uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }>(obs => {
        this._seriesSave.createAndGenerate(opts).subscribe(result => {
          obs.next({ uid, opts, result });
          obs.complete();
        });
      });
    });

    forkJoin(calls).subscribe(outcomes => this.applyRecurringOutcomes(outcomes, 'create'));
  }

  generateIgnoringConflicts(): void {
    if (this.pendingSeriesIds.length === 0 || this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];

    const calls = this.pendingSeriesIds.map(seriesId => {
      const opts = this._pendingOptsById.get(seriesId)!;
      return new Observable<{ uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }>(obs => {
        this._seriesSave.generateIgnoringConflicts(seriesId, opts).subscribe(result => {
          obs.next({ uid: opts.operatorId, opts, result });
          obs.complete();
        });
      });
    });

    forkJoin(calls).subscribe(outcomes => this.applyRecurringOutcomes(outcomes, 'retry'));
  }

  /**
   * Aggregate the per-instructor results. If any reported conflicts, show them
   * all (tagged by instructor) and keep those series pending for "Generate
   * Anyway". Otherwise close on success.
   */
  private applyRecurringOutcomes(
    outcomes: { uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }[],
    phase: 'create' | 'retry',
  ): void {
    const conflicts: any[] = [];
    const stillPending: number[] = [];
    let createdTotal = 0;
    let hadError = false;

    for (const { uid, opts, result } of outcomes) {
      if (result.kind === 'conflicts') {
        stillPending.push(result.seriesId);
        this._pendingOptsById.set(result.seriesId, opts);
        const name = this.instructorName(uid);
        for (const c of result.conflicts) {
          conflicts.push({ ...c, instructor: name });
        }
      } else if (result.kind === 'created') {
        createdTotal += result.count;
      } else {
        hadError = true;
        this.errorMessage = result.message;
      }
    }

    this.pendingSeriesIds = stillPending;

    if (conflicts.length > 0) {
      this.seriesConflicts = conflicts;
      this.isSaving = false;
      return;
    }

    this.isSaving = false;
    if (hadError) {
      return;
    }

    this._snackBar.open(
      `Created ${createdTotal} recurring schedule${createdTotal === 1 ? '' : 's'}.`,
      'OK',
      { duration: 5000 },
    );
    this.closeDialog();
  }

  cancelRecurringSave(): void {
    for (const seriesId of this.pendingSeriesIds) {
      this._seriesSave.cancel(seriesId).subscribe(() => {}, () => {});
    }
    this.seriesConflicts = [];
    this.pendingSeriesIds = [];
    this._pendingOptsById.clear();
    this.isSaving = false;
  }
}
